import { useCallback, useMemo } from 'react';

import { MentionData } from '../consts';
import {
  RECENTS_DEFAULT_MAX_LENGTH,
  RECENTS_DEFAULT_MIN_TO_SHOW,
  RECENTS_ID_PREFIX,
  useRecentsPersistence,
} from './useRecentsPersistence';

export const useMentionRecents = ({
  enabled = true,
  fieldId,
  suggestions,
  search = '',
  onAddMention: onAddMentionProp,
}: {
  enabled?: boolean;
  fieldId?: string;
  suggestions: MentionData[];
  search?: string;
  onAddMention?: (mention: MentionData) => void;
}) => {
  const { recents, addToRecents } = useRecentsPersistence(fieldId);

  const recentSuggestions = useMemo<MentionData[]>(() => {
    if (!enabled) return [];

    const lowerSearch = search.toLowerCase();

    const filtered = recents
      .map((r) => suggestions.find((s) => s.id === r.id && s.type === r.type))
      .filter((s): s is MentionData => s !== undefined)
      // When searching, only keep recents whose name matches the query
      .filter((s) => !search || s.name.toLowerCase().includes(lowerSearch));

    // When not searching, limit recents to keep the dropdown concise.
    // When searching, show every matching recent regardless of entity type.
    const visibleRecents = search
      ? filtered
      : filtered.slice(0, RECENTS_DEFAULT_MAX_LENGTH);

    // Only enforce minimum threshold when not searching
    if (!search && visibleRecents.length < RECENTS_DEFAULT_MIN_TO_SHOW) {
      return [];
    }

    return visibleRecents.map((s) => ({
      ...s,
      // renderKey holds the prefixed value and will be used as the React list key so the same
      // user can appear in both the recents section and their original group without
      // duplicate-key warnings (@draft-js-plugins/mention uses mention.id as the key).
      id: `${RECENTS_ID_PREFIX}${s.id}`,
      renderKey: s.id,
      fromRecents: true,
    }));
  }, [enabled, recents, search, suggestions]);

  const onAddMention = useCallback(
    (mention: MentionData) => {
      addToRecents({ id: mention.id, type: mention.type });
      onAddMentionProp?.(mention);
    },
    [addToRecents, onAddMentionProp],
  );

  return { recentSuggestions, onAddMention };
};
